import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Disclosure, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

import { UserRole } from '../../helpers/roles';
import { extractRoleNames, getCurrentUserDetails } from '../../redux/modules/user/actions';
import { CommonAuthComponent } from '../../Auth';
import { createTokenizedUrl, logTokenTransfer } from '../../helpers/tokenSharing';
import {
  hasAccessToEnvAdministration,
  hasAccessToEnvClinical,
  canReelMemberAccessRxAdministration,
  isUserReelMember,
} from '../../helpers/roleBasedAccessHelper';

const ENVIRONMENT_URLS = {
  clinical: {
    dev: 'https://dev.healthcompassrx.com/patients',
    staging: 'https://www.staging.healthcompassrx.com/patients',
    production: 'https://www.healthcompassrx.com/patients',
  },
  administration: {
    dev: 'https://dev-admin.healthcompassrx.com/administration?isRxAdministration=true',
    staging: 'https://staging-admin.healthcompassrx.com/administration?isRxAdministration=true',
    production: 'https://admin.healthcompassrx.com/administration?isRxAdministration=true',
  },
  reelAdministration: {
    dev: 'https://dev-admin.healthcompassrx.com/pharmacies/banner?isRxAdministration=false&reelAdminMode=true&reelAdminRedirectTarget=/pharmacies/banner',
    staging: 'https://staging-admin.healthcompassrx.com/pharmacies/banner?isRxAdministration=false&reelAdminMode=true&reelAdminRedirectTarget=/pharmacies/banner',
    production: 'https://www.admin.healthcompassrx.com/pharmacies/banner?isRxAdministration=false&reelAdminMode=true&reelAdminRedirectTarget=/pharmacies/banner',
  },
};

const PortalSelection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [redirectURL, setRedirectURL] = useState("");
  const [hasAccessToDevAdmin, setHasAccessToDevAdmin] = useState(false);
  const [hasAccessToStagingAdmin, setHasAccessToStagingAdmin] = useState(false);
  const [hasAccessToProdAdmin, setHasAccessToProdAdmin] = useState(false);
  const [hasAccessToDevClinical, setHasAccessToDevClinical] = useState(false);
  const [hasAccessToStagingClinical, setHasAccessToStagingClinical] = useState(false);
  const [hasAccessToProdClinical, setHasAccessToProdClinical] = useState(false);
  const [isDev, setIsDev] = useState(false);
  const [isStaging, setIsStaging] = useState(false);
  const [isProduction, setIsProduction] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // 'clinical' | 'admin' | null
  const hasAutoRedirectedRef = useRef(false);

  // Use the new userRoles from Redux store instead of currentUserRoles
  const { userRoles, userInfo, isAuthenticated } = useSelector((state) => state.users);
  const { currentLoggedInUserDetail } = useSelector((state) => state.currentLoggedInUserDetail);

  // Use helper-based REEL member check from current logged-in user payload.
  const isReelMember = useMemo(
    () => isUserReelMember(currentLoggedInUserDetail),
    [currentLoggedInUserDetail]
  );
  const adminPortalLabel = isReelMember ? 'Rx Administration' : 'Administration';

  useEffect(() => {
    // Check environment
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname.includes('dev') || hostname === 'localhost') {
        setIsDev(true);
        setIsStaging(false);
        setIsProduction(false);
      } else if (hostname.includes('staging')) {
        setIsDev(false);
        setIsStaging(true);
        setIsProduction(false);
      } else {
        setIsDev(false);
        setIsStaging(false);
        setIsProduction(true);
      }
    }
  }, []);

  useEffect(() => {
    // Fetch detailed user info if missing (needed for companyRole)
    if (isAuthenticated && userInfo?.userId && !currentLoggedInUserDetail) {
      dispatch(getCurrentUserDetails(userInfo.userId));
    }
  }, [isAuthenticated, userInfo?.userId, currentLoggedInUserDetail, dispatch]);

  useEffect(() => {
    if (currentLoggedInUserDetail) {
      const companyRole = currentLoggedInUserDetail?.companyRole;
      const [hasDevAdmin, hasStagingAdmin, hasProdAdmin] =
        hasAccessToEnvAdministration(companyRole);
      const [hasDevClinical, hasStagingClinical, hasProdClinical] =
        hasAccessToEnvClinical(companyRole);

      setHasAccessToDevAdmin(hasDevAdmin);
      setHasAccessToStagingAdmin(hasStagingAdmin);
      setHasAccessToProdAdmin(hasProdAdmin);
      setHasAccessToDevClinical(hasDevClinical);
      setHasAccessToStagingClinical(hasStagingClinical);
      setHasAccessToProdClinical(hasProdClinical);
    }
  }, [currentLoggedInUserDetail]);

  // Auto-navigate when only one destination is accessible — skip the selection page entirely.
  // Fires after access flags are resolved; guards prevent premature runs.
  useEffect(() => {
    if (hasAutoRedirectedRef.current) return;
    if (!currentLoggedInUserDetail) return;
    // Ensure env detection has run (all three default to false before the hostname effect fires)
    if (!isDev && !isStaging && !isProduction) return;

    const hasAnyAdmin = canAccessRxAdministration || canAccessReelAdministration;
    const totalDestinations = clinicalAccessCount + (hasAnyAdmin ? adminAccessCount : 0);
    if (totalDestinations !== 1) return;

    hasAutoRedirectedRef.current = true;

    if (clinicalAccessCount === 1) {
      handleClinicalSelection(getSingleClinicalEnvironment());
    } else if (canAccessReelAdministration) {
      handleReelAdminSelection(getSingleAdminEnvironment());
    } else if (canAccessRxAdministration) {
      handleAdminSelection(getSingleAdminEnvironment());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentLoggedInUserDetail, isDev, isStaging, isProduction,
    hasAccessToDevAdmin, hasAccessToStagingAdmin, hasAccessToProdAdmin,
    hasAccessToDevClinical, hasAccessToStagingClinical, hasAccessToProdClinical,
  ]);

  useEffect(() => {
    if (userRoles && userRoles.length > 0) {
      const roleNames = extractRoleNames(userRoles);

      if (roleNames.includes(UserRole.ReelMember)) {
        setRedirectURL("/pharmacies/banner");
      } else {
        setRedirectURL("/organizations/pharmacies");
      }
    }
  }, [userRoles]);

  const clinicalAccessCount =
    Number(hasAccessToDevClinical) +
    Number(hasAccessToStagingClinical) +
    Number(hasAccessToProdClinical);
  const hasSingleClinicalAccess = clinicalAccessCount === 1;
  const hasMultipleClinicalAccess = clinicalAccessCount > 1;
  const adminAccessCount =
    Number(hasAccessToDevAdmin) +
    Number(hasAccessToStagingAdmin) +
    Number(hasAccessToProdAdmin);
  const hasSingleAdminAccess = adminAccessCount === 1;
  const hasMultipleAdminAccess = adminAccessCount > 1;
  const hasCurrentEnvAdminAccess =
    (isDev && hasAccessToDevAdmin) ||
    (isStaging && hasAccessToStagingAdmin) ||
    (isProduction && hasAccessToProdAdmin);
  const canAccessRxAdministration =
    hasCurrentEnvAdminAccess &&
    (!isReelMember || canReelMemberAccessRxAdministration(currentLoggedInUserDetail?.companyRole));
  const canAccessReelAdministration = isReelMember && adminAccessCount > 0;

  const getCurrentEnvironmentKey = () => {
    if (isDev) return 'dev';
    if (isStaging) return 'staging';
    return 'production';
  };

  const getSingleClinicalEnvironment = () => {
    if (hasAccessToDevClinical) return 'dev';
    if (hasAccessToStagingClinical) return 'staging';
    if (hasAccessToProdClinical) return 'production';
    return getCurrentEnvironmentKey();
  };

  const getSingleAdminEnvironment = () => {
    if (hasAccessToDevAdmin) return 'dev';
    if (hasAccessToStagingAdmin) return 'staging';
    if (hasAccessToProdAdmin) return 'production';
    return getCurrentEnvironmentKey();
  };

  const switchToClinicalInCurrentEnvironment = () => {
    sessionStorage.setItem('triggerCompliance', 'true');
    navigate('/patients');
  };

  const switchToAdminInCurrentEnvironment = () => {
    const accessToken = sessionStorage.getItem('accessToken');
    const userInfo = sessionStorage.getItem('userInfo');
    const targetRoute = '/administration?isRxAdministration=true';

    if (accessToken) {
      const adminUrl = createTokenizedUrl(
        `${import.meta.env.VITE_ADMIN_APP_DOMAIN}`,
        accessToken,
        'clinical',
        userInfo,
        { returnTo: targetRoute }
      );

      logTokenTransfer('Portal Selection - Switch to Admin', 'clinical', true);
      window.location.href = adminUrl;
      return;
    }

    console.warn('No access token found, redirecting to admin login');
    window.location.href = `${import.meta.env.VITE_ADMIN_APP_DOMAIN}/administration?isRxAdministration=true`;
  };

  const switchToReelAdminInCurrentEnvironment = () => {
    const accessToken = sessionStorage.getItem('accessToken');
    const userInfo = sessionStorage.getItem('userInfo');
    const targetRoute = '/pharmacies/banner?isRxAdministration=false';

    if (accessToken) {
      const adminUrl = createTokenizedUrl(
        `${import.meta.env.VITE_ADMIN_APP_DOMAIN}`,
        accessToken,
        'clinical',
        userInfo,
        { reelAdminMode: true, returnTo: targetRoute }
      );

      logTokenTransfer('Portal Selection - Switch to REEL Admin', 'clinical', true);
      window.location.href = adminUrl;
      return;
    }

    console.warn('No access token found, redirecting to admin login');
    window.location.href = `${import.meta.env.VITE_ADMIN_APP_DOMAIN}/pharmacies/banner?isRxAdministration=false&reelAdminMode=true`;
  };

  const handleClinicalSelection = (environment) => {
    const targetEnvironment = environment || getSingleClinicalEnvironment();
    const currentEnvironmentKey = getCurrentEnvironmentKey();
    setOpenDropdown(null);

    if (targetEnvironment === currentEnvironmentKey) {
      switchToClinicalInCurrentEnvironment();
      return;
    }

    const targetUrl = ENVIRONMENT_URLS.clinical[targetEnvironment];
    if (targetUrl) {
      window.location.href = targetUrl;
    }
  };

  const handleAdminSelection = (environment) => {
    const targetEnvironment = environment || getSingleAdminEnvironment();
    const currentEnvironmentKey = getCurrentEnvironmentKey();
    setOpenDropdown(null);

    if (targetEnvironment === currentEnvironmentKey) {
      switchToAdminInCurrentEnvironment();
      return;
    }

    const targetUrl = ENVIRONMENT_URLS.administration[targetEnvironment];
    if (targetUrl) {
      window.location.href = targetUrl;
    }
  };

  const handleReelAdminSelection = (environment) => {
    const targetEnvironment = environment || getSingleAdminEnvironment();
    const currentEnvironmentKey = getCurrentEnvironmentKey();
    setOpenDropdown(null);

    if (targetEnvironment === currentEnvironmentKey) {
      switchToReelAdminInCurrentEnvironment();
      return;
    }

    const targetUrl = ENVIRONMENT_URLS.reelAdministration[targetEnvironment];
    if (targetUrl) {
      window.location.href = targetUrl;
    }
  };

  return (
    <CommonAuthComponent>

      <h1 className="text-[22px] text-center font-medium leading-[30px] mt-[20px] mb-[10px]">
        Select Your Workspace
      </h1>

      <p className="text-center text-[16px] font-normal !mb-[46px] leading-[22px]">
        You can Switch Modes in <span className="font-semibold text-teal-300">My Profile</span>.
      </p>

      <div className="flex flex-col items-center w-full space-y-4">
        {/* Clinical Services - single button for one env, dropdown for multiple */}
        {hasSingleClinicalAccess ? (
          <button
            onClick={() => handleClinicalSelection(getSingleClinicalEnvironment())}
            className="w-full max-w-[320px] flex items-center justify-center bg-REELTeal px-6 py-3.5 text-[16px] font-semibold text-white hover:bg-teal-200 focus:outline-none transition-all duration-300 rounded-[4px]"
          >
            <span>Clinicals</span>
          </button>
        ) : hasMultipleClinicalAccess ? (
          <div className="relative w-full max-w-[320px]">
            <button
              type="button"
              onClick={() =>
                setOpenDropdown((prev) => (prev === 'clinical' ? null : 'clinical'))
              }
              className={`relative flex w-full items-center justify-start bg-REELTeal px-6 py-3.5 text-[16px] font-semibold text-white hover:bg-teal-200 focus:outline-none transition-all duration-300 ${openDropdown === 'clinical' ? 'rounded-t-[4px]' : 'rounded-[4px]'
                }`}
            >
              <span>Clinicals</span>
              <ChevronDownIcon
                className={`${openDropdown === 'clinical' ? '-rotate-90 transform' : ''
                  } absolute right-4 h-5 w-5 text-white`}
              />
            </button>
            <Transition
              as={Fragment}
              show={openDropdown === 'clinical'}
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <div className="absolute left-0 right-0 top-full z-50 px-0 py-0 text-[16px] text-teal-300 bg-white border-x border-b border-REELTeal rounded-b-[4px] max-h-[220px] overflow-y-auto shadow-lg">
                <div className="flex flex-col py-1">
                  {hasAccessToDevClinical && (
                    <button
                      onClick={() => handleClinicalSelection('dev')}
                      className="w-full h-[52px] px-6 hover:bg-teal-25 font-medium transition-colors flex items-center justify-start text-left border-b border-gray-100 last:border-b-0"
                    >
                      Development
                    </button>
                  )}
                  {hasAccessToStagingClinical && (
                    <button
                      onClick={() => handleClinicalSelection('staging')}
                      className="w-full h-[52px] px-6 hover:bg-teal-25 font-medium transition-colors flex items-center justify-start text-left border-b border-gray-100 last:border-b-0"
                    >
                      Staging
                    </button>
                  )}
                  {hasAccessToProdClinical && (
                    <button
                      onClick={() => handleClinicalSelection('production')}
                      className="w-full h-[52px] px-6 hover:bg-teal-25 font-medium transition-colors flex items-center justify-start text-left border-b border-gray-100 last:border-b-0"
                    >
                      Production
                    </button>
                  )}
                </div>
              </div>
            </Transition>
          </div>
        ) : null}

        {/* Administration - single button for one env, dropdown for multiple */}
        {canAccessRxAdministration
          ? (hasSingleAdminAccess ? (
            <button
              onClick={() => handleAdminSelection(getSingleAdminEnvironment())}
              className="w-full max-w-[320px] flex items-center justify-center bg-REELTeal px-6 py-3.5 text-[16px] font-semibold text-white hover:bg-teal-200 focus:outline-none transition-all duration-300 rounded-[4px]"
            >
              <span>{adminPortalLabel}</span>
            </button>
          ) : hasMultipleAdminAccess ? (
            <div className="relative w-full max-w-[320px]">
              <button
                type="button"
                onClick={() =>
                  setOpenDropdown((prev) => (prev === 'admin' ? null : 'admin'))
                }
                className={`relative flex w-full items-center justify-start bg-REELTeal px-6 py-3.5 text-[16px] font-semibold text-white hover:bg-teal-200 focus:outline-none transition-all duration-300 ${openDropdown === 'admin' ? 'rounded-t-[4px]' : 'rounded-[4px]'
                  }`}
              >
                <span>{adminPortalLabel}</span>
                <ChevronDownIcon
                  className={`${openDropdown === 'admin' ? '-rotate-90 transform' : ''
                    } absolute right-4 h-5 w-5 text-white`}
                />
              </button>
              <Transition
                as={Fragment}
                show={openDropdown === 'admin'}
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
              >
                <div className="absolute left-0 right-0 top-full z-50 px-0 py-0 text-[16px] text-teal-300 bg-white border-x border-b border-REELTeal rounded-b-[4px] max-h-[220px] overflow-y-auto shadow-lg">
                  <div className="flex flex-col py-1">
                    {hasAccessToDevAdmin && (
                      <button
                        onClick={() => handleAdminSelection('dev')}
                        className="w-full h-[52px] px-6 hover:bg-teal-25 font-medium transition-colors flex items-center justify-start text-left border-b border-gray-100 last:border-b-0"
                      >
                        Development
                      </button>
                    )}
                    {hasAccessToStagingAdmin && (
                      <button
                        onClick={() => handleAdminSelection('staging')}
                        className="w-full h-[52px] px-6 hover:bg-teal-25 font-medium transition-colors flex items-center justify-start text-left border-b border-gray-100 last:border-b-0"
                      >
                        Staging
                      </button>
                    )}
                    {hasAccessToProdAdmin && (
                      <button
                        onClick={() => handleAdminSelection('production')}
                        className="w-full h-[52px] px-6 hover:bg-teal-25 font-medium transition-colors flex items-center justify-start text-left border-b border-gray-100 last:border-b-0"
                      >
                        Production
                      </button>
                    )}
                  </div>
                </div>
              </Transition>
            </div>
          ) : null)
          : null}

        {/* REEL Administration */}
        {canAccessReelAdministration && (
          <>
            {hasSingleAdminAccess ? (
              <button
                onClick={() => handleReelAdminSelection(getSingleAdminEnvironment())}
                className="w-full max-w-[320px] flex items-center justify-center bg-REELTeal px-6 py-3.5 text-[16px] font-semibold text-white hover:bg-teal-200 focus:outline-none transition-all duration-300 rounded-[4px]"
              >
                <span>REEL Administration</span>
              </button>
            ) : hasMultipleAdminAccess ? (
              <div className="relative w-full max-w-[320px]">
                <button
                  type="button"
                  onClick={() =>
                    setOpenDropdown((prev) => (prev === 'reel' ? null : 'reel'))
                  }
                  className={`relative flex w-full items-center justify-start bg-REELTeal px-6 py-3.5 text-[16px] font-semibold text-white hover:bg-teal-200 focus:outline-none transition-all duration-300 ${openDropdown === 'reel' ? 'rounded-t-[4px]' : 'rounded-[4px]'
                    }`}
                >
                  <span>REEL Administration</span>
                  <ChevronDownIcon
                    className={`${openDropdown === 'reel' ? '-rotate-90 transform' : ''
                      } absolute right-4 h-5 w-5 text-white`}
                  />
                </button>
                <Transition
                  as={Fragment}
                  show={openDropdown === 'reel'}
                  enter="transition duration-100 ease-out"
                  enterFrom="transform scale-95 opacity-0"
                  enterTo="transform scale-100 opacity-100"
                  leave="transition duration-75 ease-out"
                  leaveFrom="transform scale-100 opacity-100"
                  leaveTo="transform scale-95 opacity-0"
                >
                  <div className="absolute left-0 right-0 top-full z-50 px-0 py-0 text-[16px] text-teal-300 bg-white border-x border-b border-REELTeal rounded-b-[4px] max-h-[220px] overflow-y-auto shadow-lg">
                    <div className="flex flex-col py-1">
                      {hasAccessToDevAdmin && (
                        <button
                          onClick={() => handleReelAdminSelection('dev')}
                          className="w-full h-[52px] px-6 hover:bg-teal-25 font-medium transition-colors flex items-center justify-start text-left border-b border-gray-100 last:border-b-0"
                        >
                          Development
                        </button>
                      )}
                      {hasAccessToStagingAdmin && (
                        <button
                          onClick={() => handleReelAdminSelection('staging')}
                          className="w-full h-[52px] px-6 hover:bg-teal-25 font-medium transition-colors flex items-center justify-start text-left border-b border-gray-100 last:border-b-0"
                        >
                          Staging
                        </button>
                      )}
                      {hasAccessToProdAdmin && (
                        <button
                          onClick={() => handleReelAdminSelection('production')}
                          className="w-full h-[52px] px-6 hover:bg-teal-25 font-medium transition-colors flex items-center justify-start text-left border-b border-gray-100 last:border-b-0"
                        >
                          Production
                        </button>
                      )}
                    </div>
                  </div>
                </Transition>
              </div>
            ) : null}
          </>
        )}
      </div>
    </CommonAuthComponent>
  );
};

export default PortalSelection;
